import { useState } from 'react';
import { PRODUCT_PERMISSIONS } from '@/constants/permissions';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { bookTypesInfiniteQueryOptions } from '../api/product.api';
import { CatalogueListLayout } from '@/components/common/CatalogueListLayout';
import { getBookTypeColumns } from '../components/CatalogueColumns';
import { BookTypeFormModal } from '../components/CatalogueFormModal';
import { useCatalogueNameSearch } from '../hooks/useCatalogueNameSearch';
import type { BookType } from '../types/product.types';
import { getBookTypeStatCards } from '../utils/productStatCards';

export function BookTypePage() {
  const controller = useCatalogueNameSearch('bookTypes');
  const { rows, firstPageMeta, isLoading, isRefetching, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(bookTypesInfiniteQueryOptions(controller.search));

  const [editing, setEditing] = useState<BookType | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);

  const columns = getBookTypeColumns(controller.search.search, {
    onEdit: (row) => {
      setEditing(row);
      setFormOpen(true);
    },
  });

  return (
    <CatalogueListLayout
      title="Book Type"
      search={{
        value: controller.searchText,
        onChange: controller.setSearchText,
        placeholder: 'Search book types',
      }}
      stats={{
        cards: getBookTypeStatCards(firstPageMeta),
        isLoading,
        isFetching: isRefetching,
        isError: loadError !== null,
      }}
      addRequires={PRODUCT_PERMISSIONS.BOOK_TYPE_CREATE}
      onAdd={() => {
        setEditing(null);
        setFormOpen(true);
      }}
      addLabel="Add Book Type"
    >
      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the book types"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          className="lg:flex-1"
          stickyColumnId="name"
          emptyMessage="No book type matches"
          {...loadMoreProps}
        />
      )}

      <BookTypeFormModal bookType={editing} open={isFormOpen} onOpenChange={setFormOpen} />
    </CatalogueListLayout>
  );
}
